/**
 * P3-40: BadgesPanel —— 徽章面板。
 *
 * 逐结构对齐 `js/achievements.js:726-815`：
 * - 头部显示完成徽章数 / 总数
 * - 全部徽章渲染，未解锁显示 ???
 * - fantasticFurColor 已解锁时根据 leader color/variety 追加动态类
 * - fractured pact 开启时混淆 leader 信息
 */
import { useGameTick } from "../../bridge/useGameTick";
import { t } from "../../bridge/i18n";
import type { GamePage } from "../../bridge/GameBridge";

interface BadgeModel {
  name: string;
  title: string;
  description: string;
  unlocked: boolean;
}

interface BadgePanelSnapshot {
  header: string;
  badges: BadgeModel[];
  leader: Record<string, unknown> | null;
  fractured: boolean;
}

function getBadgeCSSClass(badge: BadgeModel, leader: Record<string, unknown> | null, fractured: boolean): string {
  let className = "achievement badge";
  if (!badge.unlocked) {
    return className;
  }
  className += " unlocked";

  if (badge.name !== "fantasticFurColor" || !leader) {
    return className;
  }

  const colorToUse = fractured ? (leader.fakeColor as number) : (leader.color as number);
  const varietyToUse = fractured ? (leader.fakeVariety as number) : (leader.variety as number);

  const colors = (leader.colors as Array<{ color: string }> | undefined) ?? [];
  const varieties = (leader.varieties as Array<{ style: string }> | undefined) ?? [];

  const colorStr =
    colorToUse !== undefined && colors[colorToUse + 1]
      ? colors[colorToUse + 1].color
      : "none";
  const varietyStr =
    varietyToUse !== undefined && varieties[varietyToUse + 1]
      ? varieties[varietyToUse + 1].style
      : "none";

  if (colorStr !== "none" || varietyStr !== "none") {
    className += " name color-" + colorStr + " variety-" + varietyStr;
  }

  return className;
}

function getBadgesSnapshot(game: GamePage | null): BadgePanelSnapshot | null {
  if (!game) {
    return null;
  }

  const badges = (game.achievements?.badges as BadgeModel[] | undefined) ?? [];
  const totalBadges = badges.length;
  const completedBadges = badges.filter((b) => b.unlocked).length;

  const header = t(
    "badges.header",
    [completedBadges, totalBadges],
  ) ?? `badges.header ${completedBadges} ${totalBadges}`;

  const leader = (game.village?.leader as Record<string, unknown> | null) ?? null;
  const fractured =
    Boolean(game.religion?.getPact?.("fractured")?.val) &&
    Boolean(game.getFeatureFlag?.("MAUSOLEUM_PACTS"));

  return { header, badges, leader, fractured };
}

function snapEq(a: BadgePanelSnapshot | null, b: BadgePanelSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  if (a.header !== b.header || a.fractured !== b.fractured || a.badges.length !== b.badges.length) {
    return false;
  }
  for (let i = 0; i < a.badges.length; i++) {
    const aa = a.badges[i];
    const bb = b.badges[i];
    if (
      aa.name !== bb.name ||
      aa.title !== bb.title ||
      aa.description !== bb.description ||
      aa.unlocked !== bb.unlocked
    ) {
      return false;
    }
  }
  if (a.leader === b.leader) {
    return true;
  }
  if (!a.leader || !b.leader) {
    return false;
  }
  if (
    a.leader.color !== b.leader.color ||
    a.leader.variety !== b.leader.variety ||
    a.leader.fakeColor !== b.leader.fakeColor ||
    a.leader.fakeVariety !== b.leader.fakeVariety
  ) {
    return false;
  }
  return true;
}

export default function BadgesPanel() {
  const snapshot = useGameTick(
    (game) => getBadgesSnapshot(game as GamePage | null),
    snapEq,
  );

  if (!snapshot) {
    return null;
  }

  return (
    <div className="badges-container">
      <div className="achievement-header">{snapshot.header}</div>
      {snapshot.badges.map((badge) => {
        const className = getBadgeCSSClass(badge, snapshot.leader, snapshot.fractured);
        const title = badge.unlocked ? badge.description : "???";
        const content = badge.unlocked ? badge.title : "???";

        return (
          <span
            key={badge.name}
            className={className}
            title={title}
            dangerouslySetInnerHTML={{ __html: content }}
          />
        );
      })}
    </div>
  );
}
